import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';

import ConfirmButton from '~/components/Common/Elements/ConfirmButton';
import GridPagination from '~/components/Common/Elements/GridPagination';
import type { CharterCabinClass } from '~/components/Flights/Charter/charter.types';

import {
  useGetCharterPricingInventoryList,
  useUpsertCharterPricingInventory,
} from '~/hooks/apis/flights-charter/useCharterPricingInventory';
import {
  useCreateCharterReferral,
  useDeleteCharterReferral,
  useGetCharterReferralList,
  useUpdateCharterReferral,
} from '~/hooks/apis/flights-charter/useCharterReferral';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { toContractCabinClass } from '~/services/CharterPricingInventoryService';
import * as TimeService from '~/services/TimeService';

import type { CharterPricingInventoryRow, UpsertCharterPricingInventoryBody } from '~/types/charterPricingInventory';
import type { CharterReferralRow, CreateCharterReferralBody, UpdateCharterReferralBody } from '~/types/charterReferral';

import CharterPricingInventoryDialog, { type PricingInventoryFormValues } from './CharterPricingInventoryDialog';
import CharterReferralDialog from './CharterReferralDialog';
import { charterAirportOptions, charterCabinOptions } from './charterAirports';

const pageSizeOptions = [5, 10, 25];
const tableHeight = 452;
const emptyReferralFormValues: CreateCharterReferralBody = {
  fnfName: '',
  fnfEmail: '',
  referralName: '',
  referralEmail: '',
};
const defaultPricingInventoryDepDate = '2026-05-18';

const emptyPricingInventoryFormValues: PricingInventoryFormValues = {
  depAirport: 'MEL',
  arrAirport: 'MLE',
  depDate: defaultPricingInventoryDepDate,
  cabin: '',
  availableDaysBefore: '',
  promoCodeDaysBefore: '',
  waitlistSeat: '',
  adultPrice: '',
  childPrice: '',
  infantPrice: '',
};

function getAlternateAirport(airport: string): string {
  return airport === charterAirportOptions[0] ? charterAirportOptions[1] : charterAirportOptions[0];
}

function normalizePricingInventoryAirports(values: PricingInventoryFormValues): PricingInventoryFormValues {
  const depAirport = charterAirportOptions.includes(values.depAirport as (typeof charterAirportOptions)[number])
    ? values.depAirport
    : charterAirportOptions[0];
  const arrAirport =
    depAirport === values.arrAirport
      ? getAlternateAirport(depAirport)
      : values.arrAirport || getAlternateAirport(depAirport);

  return {
    ...values,
    depAirport,
    arrAirport,
  };
}

const tableCardHeaderSx = {
  p: 0,
  pb: 1,
  '& .MuiCardHeader-content': {
    my: 0,
  },
  '& .MuiCardHeader-action': {
    m: 0,
    alignSelf: 'center',
  },
};

function toPricingInventoryFormValues(row: CharterPricingInventoryRow): PricingInventoryFormValues {
  return normalizePricingInventoryAirports({
    depAirport: row.depAirport || 'MEL',
    arrAirport: row.arrAirport || 'MLE',
    depDate: row.date || defaultPricingInventoryDepDate,
    cabin: row.cabin,
    availableDaysBefore: String(row.availableDaysBefore),
    promoCodeDaysBefore: String(row.promoCodeDaysBefore),
    waitlistSeat: String(row.waitlistSeat),
    adultPrice: String(row.adultPrice),
    childPrice: String(row.childPrice),
    infantPrice: String(row.infantPrice),
  });
}

function toPricingInventoryPayload(values: PricingInventoryFormValues): UpsertCharterPricingInventoryBody {
  return {
    depAirport: values.depAirport.trim(),
    arrAirport: values.arrAirport.trim(),
    depDate: values.depDate.trim(),
    cabinClass: toContractCabinClass(values.cabin as CharterCabinClass),
    allocation: Number(values.waitlistSeat),
    availableDaysBefore: Number(values.availableDaysBefore),
    promoCodeDaysBefore: Number(values.promoCodeDaysBefore),
    pricing: [
      // TODO: Replace temporary fixed tax/currency defaults once backend/product confirms the real values.
      { paxType: 'ADT', price: Number(values.adultPrice), tax: 0, currency: 'AUD' },
      { paxType: 'CHD', price: Number(values.childPrice), tax: 0, currency: 'AUD' },
      { paxType: 'INF', price: Number(values.infantPrice), tax: 0, currency: 'AUD' },
    ],
  };
}

export default function CharterFriendAndFamilyPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { paginationModel: pricingInventoryPaginationModel, setPaginationModel: setPricingInventoryPaginationModel } =
    useDataGridPagination({
      page: 0,
      pageSize: 10,
    });
  const { paginationModel: referralPaginationModel, setPaginationModel: setReferralPaginationModel } =
    useDataGridPagination({
      page: 0,
      pageSize: 10,
    });
  const [isCreatePricingInventoryDialogOpen, setIsCreatePricingInventoryDialogOpen] = useState(false);
  const [pricingInventoryFormValues, setPricingInventoryFormValues] = useState<PricingInventoryFormValues>(
    emptyPricingInventoryFormValues,
  );
  const [isCreateReferralDialogOpen, setIsCreateReferralDialogOpen] = useState(false);
  const [referralFormValues, setReferralFormValues] = useState<CreateCharterReferralBody>(emptyReferralFormValues);
  const [editingPricingInventoryRow, setEditingPricingInventoryRow] = useState<CharterPricingInventoryRow | null>(null);
  const [editingReferralRow, setEditingReferralRow] = useState<CharterReferralRow | null>(null);
  const [selectedReferralIds, setSelectedReferralIds] = useState<GridRowSelectionModel>([]);
  const [pricingInventoryCabinFilter, setPricingInventoryCabinFilter] = useState<'all' | CharterCabinClass>('all');
  const [pricingInventoryFilterAnchorEl, setPricingInventoryFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const referralOffset = referralPaginationModel.page * referralPaginationModel.pageSize;
  const upsertPricingInventoryMutation = useUpsertCharterPricingInventory();
  const createReferralMutation = useCreateCharterReferral();
  const deleteReferralMutation = useDeleteCharterReferral();
  const updateReferralMutation = useUpdateCharterReferral();

  const pricingInventoryListQuery = useGetCharterPricingInventoryList();
  const referralListQuery = useGetCharterReferralList({
    offset: referralOffset,
    limit: referralPaginationModel.pageSize,
  });

  const pricingInventoryRows = useMemo(
    () => pricingInventoryListQuery.data?.result ?? [],
    [pricingInventoryListQuery.data?.result],
  );
  const filteredPricingInventoryRows = useMemo(() => {
    if (pricingInventoryCabinFilter === 'all') {
      return pricingInventoryRows;
    }

    return pricingInventoryRows.filter((row) => row.cabin === pricingInventoryCabinFilter);
  }, [pricingInventoryCabinFilter, pricingInventoryRows]);
  const referralRows = useMemo(() => referralListQuery.data?.result ?? [], [referralListQuery.data?.result]);
  const referralRowCount = referralListQuery.data?.pagination.totalItems ?? 0;

  const resetReferralForm = useCallback(() => {
    setReferralFormValues(emptyReferralFormValues);
  }, []);
  const resetPricingInventoryForm = useCallback(() => {
    setPricingInventoryFormValues(normalizePricingInventoryAirports(emptyPricingInventoryFormValues));
  }, []);
  const handlePricingInventoryFormChange = useCallback((field: keyof PricingInventoryFormValues, value: string) => {
    setPricingInventoryFormValues((current) => {
      if (field === 'depAirport') {
        return normalizePricingInventoryAirports({
          ...current,
          depAirport: value,
          arrAirport: getAlternateAirport(value),
        });
      }

      if (field === 'arrAirport') {
        return normalizePricingInventoryAirports({
          ...current,
          arrAirport: value,
          depAirport: getAlternateAirport(value),
        });
      }

      return {
        ...current,
        [field]: value,
      };
    });
  }, []);

  const handleOpenCreateReferralDialog = useCallback(() => {
    setEditingReferralRow(null);
    resetReferralForm();
    setIsCreateReferralDialogOpen(true);
  }, [resetReferralForm]);

  const handleOpenCreatePricingInventoryDialog = useCallback(() => {
    setEditingPricingInventoryRow(null);
    resetPricingInventoryForm();
    setIsCreatePricingInventoryDialogOpen(true);
  }, [resetPricingInventoryForm]);
  const handleOpenPricingInventoryFilter = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setPricingInventoryFilterAnchorEl(event.currentTarget);
  }, []);
  const handleClosePricingInventoryFilter = useCallback(() => {
    setPricingInventoryFilterAnchorEl(null);
  }, []);
  const handlePricingInventoryCabinFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value as 'all' | CharterCabinClass;
      setPricingInventoryCabinFilter(nextValue);
      setPricingInventoryPaginationModel((current) => ({ ...current, page: 0 }));
      setPricingInventoryFilterAnchorEl(null);
    },
    [setPricingInventoryPaginationModel],
  );

  const handleCloseCreateReferralDialog = useCallback(() => {
    setIsCreateReferralDialogOpen(false);
    setEditingReferralRow(null);
    resetReferralForm();
  }, [resetReferralForm]);
  const handleCloseCreatePricingInventoryDialog = useCallback(() => {
    setIsCreatePricingInventoryDialogOpen(false);
    setEditingPricingInventoryRow(null);
    resetPricingInventoryForm();
  }, [resetPricingInventoryForm]);

  const handleUpsertPricingInventory = useCallback(async () => {
    const normalizedValues = toPricingInventoryPayload(pricingInventoryFormValues);

    try {
      await upsertPricingInventoryMutation.mutateAsync(normalizedValues);
      enqueueSnackbar(
        editingPricingInventoryRow ? 'Pricing row updated successfully.' : 'Pricing row created successfully.',
        { variant: 'success' },
      );
      handleCloseCreatePricingInventoryDialog();
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : editingPricingInventoryRow
          ? 'Unable to update pricing row.'
          : 'Unable to create pricing row.',
        {
          variant: 'error',
        },
      );
    }
  }, [
    editingPricingInventoryRow,
    enqueueSnackbar,
    handleCloseCreatePricingInventoryDialog,
    pricingInventoryFormValues,
    upsertPricingInventoryMutation,
  ]);

  const handleCreateReferral = useCallback(async () => {
    const normalizedValues: CreateCharterReferralBody = {
      fnfName: referralFormValues.fnfName.trim(),
      fnfEmail: referralFormValues.fnfEmail.trim().toLowerCase(),
      referralName: referralFormValues.referralName.trim(),
      referralEmail: referralFormValues.referralEmail.trim().toLowerCase(),
    };

    try {
      await createReferralMutation.mutateAsync(normalizedValues);
      enqueueSnackbar('Referral created successfully.', { variant: 'success' });
      handleCloseCreateReferralDialog();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to create referral.', {
        variant: 'error',
      });
    }
  }, [createReferralMutation, enqueueSnackbar, handleCloseCreateReferralDialog, referralFormValues]);

  const handleUpdateReferral = useCallback(async () => {
    if (!editingReferralRow) {
      return;
    }

    const normalizedValues: UpdateCharterReferralBody = {
      fnfReferralId: editingReferralRow.id,
      fnfName: referralFormValues.fnfName.trim(),
      fnfEmail: referralFormValues.fnfEmail.trim().toLowerCase(),
      referralName: referralFormValues.referralName.trim(),
    };

    try {
      await updateReferralMutation.mutateAsync({ body: normalizedValues });
      enqueueSnackbar('Referral updated successfully.', { variant: 'success' });
      handleCloseCreateReferralDialog();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to update referral.', {
        variant: 'error',
      });
    }
  }, [
    editingReferralRow,
    enqueueSnackbar,
    handleCloseCreateReferralDialog,
    referralFormValues,
    updateReferralMutation,
  ]);

  const handleEditPricingInventory = useCallback((row: CharterPricingInventoryRow) => {
    setEditingPricingInventoryRow(row);
    setPricingInventoryFormValues(toPricingInventoryFormValues(row));
    setIsCreatePricingInventoryDialogOpen(true);
  }, []);

  const handleEditReferral = useCallback((row: CharterReferralRow) => {
    setEditingReferralRow(row);
    setReferralFormValues({
      fnfName: row.fnfName,
      fnfEmail: row.fnfEmail,
      referralName: row.referralName,
      referralEmail: row.referralEmail,
    });
    setIsCreateReferralDialogOpen(true);
  }, []);

  const handleSubmitPricingInventoryDialog = useCallback(async () => {
    await handleUpsertPricingInventory();
  }, [handleUpsertPricingInventory]);

  const handleSubmitReferralDialog = useCallback(async () => {
    if (editingReferralRow) {
      await handleUpdateReferral();
      return;
    }

    await handleCreateReferral();
  }, [editingReferralRow, handleCreateReferral, handleUpdateReferral]);

  const handleConfirmDeleteReferral = useCallback(async () => {
    const selectedIds = selectedReferralIds.map(String);
    if (selectedIds.length === 0) {
      return;
    }

    try {
      await deleteReferralMutation.mutateAsync(selectedIds);
      enqueueSnackbar('Selected referrals deleted successfully.', { variant: 'success' });
      setSelectedReferralIds([]);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to delete selected referrals.', {
        variant: 'error',
      });
    }
  }, [deleteReferralMutation, enqueueSnackbar, selectedReferralIds]);

  const pricingInventoryColumns = useMemo<Array<GridColDef<CharterPricingInventoryRow>>>(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        width: 110,
        align: 'left',
        headerAlign: 'left',
      },
      { field: 'route', headerName: 'Route', width: 110 },
      { field: 'cabin', headerName: 'Cabin', width: 130, align: 'left', headerAlign: 'left' },
      {
        field: 'availableDaysBefore',
        headerName: 'Available Days Before',
        width: 130,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'promoCodeDaysBefore',
        headerName: 'Promo Code Days Before',
        width: 130,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'waitlistSeat',
        headerName: 'Waitlist Seat',
        width: 115,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'adultPrice',
        headerName: 'Adult Price',
        width: 110,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'childPrice',
        headerName: 'Child Price',
        width: 110,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'infantPrice',
        headerName: 'Infant Price',
        width: 110,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'totalBooked',
        headerName: 'Total Booked',
        width: 120,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'employee',
        headerName: 'Employee (No Promo Code)',
        width: 105,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) => row.employee - row.employeeWithPromoCode,
      },
      {
        field: 'employeeWithPromoCode',
        headerName: 'Employee (Promo Code)',
        width: 130,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'friendAndFamily',
        headerName: 'Friends and Family',
        width: 120,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'openForSale',
        headerName: 'Open for Sale',
        width: 120,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'edit',
        headerName: 'Edit',
        width: 100,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button
              size="small"
              onClick={() => handleEditPricingInventory(row)}
              startIcon={<EditIcon fontSize="small" />}
            >
              Edit
            </Button>
          </Box>
        ),
      },
    ],
    [handleEditPricingInventory],
  );

  const referralColumns = useMemo<Array<GridColDef<CharterReferralRow>>>(
    () => [
      { field: 'fnfName', headerName: 'Referee Name', minWidth: 180, flex: 1 },
      { field: 'fnfEmail', headerName: 'Referee Email', minWidth: 220, flex: 1 },
      { field: 'referralName', headerName: 'Referral Name', minWidth: 180, flex: 1 },
      { field: 'referralEmail', headerName: 'Referral Email', minWidth: 220, flex: 1 },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 170,
        valueFormatter: (value) => (typeof value === 'string' ? TimeService.formatDateWithClock(value) : ''),
      },
      {
        field: 'edit',
        headerName: 'Edit',
        width: 100,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button size="small" onClick={() => handleEditReferral(row)} startIcon={<EditIcon fontSize="small" />}>
              Edit
            </Button>
          </Box>
        ),
      },
    ],
    [handleEditReferral],
  );

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h3" fontWeight={600}>
          Charter Friends and Family
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preview pricing and inventory rows for the friends and family workflow.
        </Typography>
      </Stack>

      <Card>
        <CardHeader
          title="Pricing and Inventory"
          action={
            <Stack direction="row" spacing={1} sx={{ pb: 1 }}>
              <Button onClick={handleOpenCreatePricingInventoryDialog}>Add Pricing Row</Button>
              <Button color="error" variant="contained" disabled>
                Delete Selected
              </Button>
            </Stack>
          }
          slotProps={{ title: { variant: 'h4' } }}
          sx={tableCardHeaderSx}
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {pricingInventoryListQuery.error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">Unable to load pricing and inventory rows.</Alert>
            </Box>
          ) : null}
          <Box sx={{ px: 2, pb: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenPricingInventoryFilter}
              startIcon={<FilterListIcon />}
            >
              Filter
            </Button>
            <Popover
              open={Boolean(pricingInventoryFilterAnchorEl)}
              anchorEl={pricingInventoryFilterAnchorEl}
              onClose={handleClosePricingInventoryFilter}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { mt: 1, p: 2, minWidth: 220 } } }}
            >
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Cabin Class
                </Typography>
                <RadioGroup value={pricingInventoryCabinFilter} onChange={handlePricingInventoryCabinFilterChange}>
                  <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                  {charterCabinOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </Stack>
            </Popover>
          </Box>
          <Box sx={{ height: tableHeight, width: '100%' }}>
            <DataGrid
              rows={filteredPricingInventoryRows}
              columns={pricingInventoryColumns}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'date', sort: 'asc' }],
                },
              }}
              pagination
              paginationModel={pricingInventoryPaginationModel}
              onPaginationModelChange={setPricingInventoryPaginationModel}
              paginationMode="client"
              pageSizeOptions={pageSizeOptions}
              slots={{ pagination: GridPagination }}
              density="compact"
              loading={pricingInventoryListQuery.isLoading}
              disableRowSelectionOnClick
              disableColumnMenu
            />
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Referral"
          action={
            <Stack direction="row" spacing={1} sx={{ pb: 1 }}>
              <Button onClick={handleOpenCreateReferralDialog}>Add Referral</Button>
              <ConfirmButton
                color="error"
                variant="contained"
                disabled={selectedReferralIds.length === 0 || deleteReferralMutation.isPending}
                confirmTitle="Delete referrals"
                confirmQuestion="Are you sure you want to delete the selected referrals?"
                confirmAnswer="Yes, delete selected"
                onConfirm={handleConfirmDeleteReferral}
                position="top"
              >
                Delete Selected
              </ConfirmButton>
            </Stack>
          }
          slotProps={{ title: { variant: 'h4' } }}
          sx={tableCardHeaderSx}
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {referralListQuery.error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">Unable to load referral rows.</Alert>
            </Box>
          ) : null}
          <Box sx={{ height: tableHeight, width: '100%' }}>
            <DataGrid
              rows={referralRows}
              columns={referralColumns}
              pagination
              paginationModel={referralPaginationModel}
              onPaginationModelChange={setReferralPaginationModel}
              paginationMode="server"
              pageSizeOptions={pageSizeOptions}
              slots={{ pagination: GridPagination }}
              density="compact"
              checkboxSelection
              rowCount={referralRowCount}
              loading={referralListQuery.isLoading}
              rowSelectionModel={selectedReferralIds}
              onRowSelectionModelChange={setSelectedReferralIds}
              disableRowSelectionOnClick
              disableColumnMenu
            />
          </Box>
        </CardContent>
      </Card>

      <CharterReferralDialog
        open={isCreateReferralDialogOpen}
        loading={editingReferralRow ? updateReferralMutation.isPending : createReferralMutation.isPending}
        values={referralFormValues}
        title={editingReferralRow ? 'Edit Referral' : 'Add Referral'}
        submitLabel={editingReferralRow ? 'Save Changes' : 'Create Referral'}
        isEditMode={Boolean(editingReferralRow)}
        existingRows={referralRows}
        onClose={handleCloseCreateReferralDialog}
        onSubmit={handleSubmitReferralDialog}
        onChange={(field, value) => setReferralFormValues((current) => ({ ...current, [field]: value }))}
      />
      <CharterPricingInventoryDialog
        open={isCreatePricingInventoryDialogOpen}
        loading={upsertPricingInventoryMutation.isPending}
        values={pricingInventoryFormValues}
        existingRows={pricingInventoryRows}
        isEditMode={Boolean(editingPricingInventoryRow)}
        minimumWaitlistSeat={editingPricingInventoryRow?.totalBooked ?? 0}
        onClose={handleCloseCreatePricingInventoryDialog}
        onSubmit={handleSubmitPricingInventoryDialog}
        title={editingPricingInventoryRow ? 'Edit Pricing Row' : 'Add Pricing Row'}
        submitLabel={editingPricingInventoryRow ? 'Save Changes' : 'Create Pricing Row'}
        onChange={handlePricingInventoryFormChange}
      />
    </Stack>
  );
}
